import { SmallContentCardComponentProps } from './small-content-card.component'
import { convertSecondsToDuration } from '../../../shared/functions/date-time'
import { ContentCardItem, mapVideoToPrimaryAssociationLink } from '../content-card.utility'
import { WatchListItemAnalytics } from '../../../watch/utility/watch.interfaces'
import { AnalyticsModel, LinkModel } from '@flocasts/experience-service-types'
import { getSubtitleWithRelativeDate } from '../../../shared/utility-functions/string.utility'

const mapDisplayItemToAnalytics = (
  item: ContentCardItem,
  listItemAnalytics: WatchListItemAnalytics
): AnalyticsModel => {
  switch (item.type) {
    case 'card':
    case 'collection':
      return {
        ...listItemAnalytics,
        type: 'analytics',
        siteId: null,
        siteName: null,
        nodeId: item.action.analytics?.node_id || null,
        nodeType: item.action.analytics?.node_type || null,
        primaryAssociationId: item.action.analytics?.primary_association_id || null,
        primaryAssociationTitle: item.action.analytics?.primary_association_title || null,
        entityType: item.action.analytics?.node_type || null,
        entityId: item.action.analytics?.node_id || null,
        name: null,
        collectionSlug: null,
        isPremium: item.action.analytics?.is_premium || false,
        extraContext: null,
        primaryAssociationShortTitle: null,
        slugUri: null,
        videoAnalytics: null
      }
    default:
      return {
        ...listItemAnalytics,
        type: 'analytics',
        siteId: null,
        siteName: null,
        nodeId: item.id,
        nodeType: item.type,
        primaryAssociationId: item.node.primary_event_or_series_association?.node_id ?? null,
        primaryAssociationTitle: item.node.primary_event_or_series_association?.title ?? null,
        entityType: item.type,
        entityId: item.id,
        name: null,
        collectionSlug: null,
        isPremium: item.premium,
        extraContext: null,
        primaryAssociationShortTitle: null,
        slugUri: null,
        videoAnalytics: null
      }
  }
}

const mapDisplayItemToAction = (item: ContentCardItem, listItemAnalytics: WatchListItemAnalytics): LinkModel => {
  switch (item.type) {
    case 'card':
    case 'collection':
      return {
        type: 'link',
        screenType: 'unknown',
        id: 'non-unique-id',
        title: null,
        url: item.action?.url,
        style: 'open',
        nextStyle: null,
        shareUrl: item.action?.url,
        state: null,
        apiCall: null,
        mergeParams: null,
        contentMetadata: null,
        nodeId: item.action.analytics?.node_id || null,
        nodeType: item.action.analytics?.node_type || null,
        analytics: mapDisplayItemToAnalytics(item, listItemAnalytics),
        asset: null,
        secondaryAsset: null,
        target: null
      }
    default:
      return {
        type: 'link',
        screenType: 'unknown',
        id: 'non-unique-id',
        title: null,
        url: mapVideoToPrimaryAssociationLink(item),
        style: 'open',
        nextStyle: null,
        nodeId: item.id,
        nodeType: item.type,
        shareUrl: mapVideoToPrimaryAssociationLink(item),
        state: null,
        apiCall: null,
        mergeParams: null,
        contentMetadata: null,
        analytics: mapDisplayItemToAnalytics(item, listItemAnalytics),
        asset: null,
        secondaryAsset: null,
        target: null
      }
  }
}

/**
 * Utility function to map different types of possible watch items
 * into the SmallContentCard display component
 */
export const mapDisplayItemToSmallContentCard = (
  item: ContentCardItem,
  listItemAnalytics: WatchListItemAnalytics,
  userIsPremium: boolean
): SmallContentCardComponentProps => {
  switch (item.type) {
    case 'card':
      return {
        id: item.id,
        type: item.type,
        title: item.title,
        style: 'small-card',
        previewVideoMetadata: null,
        subtitle1: getSubtitleWithRelativeDate(
          item.subtitle_1_parts.display_date,
          item.subtitle_1_parts.is_premium,
          userIsPremium
        ),
        subtitle2: null,
        subtitle1Parts: null,
        showPlayIcon: false,
        description: item.description,
        previewImage: {
          url: item.preview_image.url,
          type: 'image',
          id: 'non-unique-id',
          altText: null,
          style: 'standard',
          zoom: 1
        },
        label1: item.label_1,
        action: mapDisplayItemToAction(item, listItemAnalytics),
        iconImage: null
      }
    case 'collection':
      return {
        id: item.id,
        type: item.type,
        title: item.title,
        style: 'small-card',
        previewVideoMetadata: null,
        subtitle1: item.footer_1,
        subtitle2: null,
        subtitle1Parts: null,
        showPlayIcon: false,
        description: item.description,
        previewImage: {
          url: item.preview_image.url,
          id: 'non-unique-id',
          type: 'image',
          altText: null,
          style: 'standard',
          zoom: 1
        },
        label1: item.label_1,
        action: mapDisplayItemToAction(item, listItemAnalytics),
        iconImage: null
      }
    case 'video':
      return {
        id: `small-card-${item.id}`,
        type: item.type,
        title: item.short_title || item.title,
        style: 'small-card',
        previewVideoMetadata: null,
        subtitle1: getSubtitleWithRelativeDate(item.publish_start_date, item.premium, userIsPremium),
        subtitle2: null,
        subtitle1Parts: null,
        showPlayIcon: true,
        description: null,
        previewImage: {
          url: item.asset.url,
          id: 'non-unique-id',
          type: 'image',
          altText: null,
          style: 'standard',
          zoom: 1
        },
        label1: convertSecondsToDuration(item.duration),
        action: mapDisplayItemToAction(item, listItemAnalytics),
        iconImage: null
      }
  }
}
